/**
 * Dominican taxpayer identifier (RNC / cédula) validation utilities (CLI-2).
 *
 * This module mirrors the backend validator in `src/server/models/Cliente.js`
 * EXACTLY so the frontend can never drift from the server-side rule:
 *
 * - 9-digit RNC: canonical DGII modulo-11 checksum. Weights [7,9,8,6,5,4,3,2]
 *   over digits 1-8; the 9th digit is the check digit. Remainder 0 -> 2,
 *   1 -> 1, otherwise 11 - remainder.
 * - 11-digit: cédula (Luhn mod-10). The "extended modulo-11 RNC" form (weights
 *   over digits 1-8 with digits 9-10 at weight 1) is NOT a real DGII format
 *   and is rejected, exactly like the backend.
 *
 * Separators (dashes, spaces) are stripped before validating.
 */

const PESOS_MODULO_11 = [7, 9, 8, 6, 5, 4, 3, 2];

/** Strips every non-digit character (dashes, spaces, prefixes like "RNC"). */
export function normalizarRNC(rnc: string): string {
  return String(rnc).replace(/\D/g, '');
}

/** DGII modulo-11 checksum over an 8-digit body; the 9th digit is the check. */
export function validarRNCModulo11(digitos: string): boolean {
  let suma = 0;
  for (let i = 0; i < 8; i += 1) {
    suma += Number(digitos[i]) * PESOS_MODULO_11[i];
  }

  const residuo = suma % 11;
  const digitoCalculado = residuo === 0 ? 2 : residuo === 1 ? 1 : 11 - residuo;

  return digitoCalculado === Number(digitos[digitos.length - 1]);
}

/** Luhn mod-10 checksum for the 11-digit cédula format. */
export function validarCedulaLuhn(digitos: string): boolean {
  let suma = 0;
  let duplicar = false;
  for (let i = digitos.length - 1; i >= 0; i -= 1) {
    let d = Number(digitos[i]);
    if (duplicar) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    suma += d;
    duplicar = !duplicar;
  }
  return suma % 10 === 0;
}

/**
 * Validates a Dominican taxpayer identifier: 9-digit RNC (DGII modulo-11) or
 * 11-digit cédula (Luhn). Any other length is invalid.
 */
export function validarRNC(rnc: string): boolean {
  const digitos = normalizarRNC(rnc);
  if (digitos.length === 9) {
    return validarRNCModulo11(digitos);
  }
  if (digitos.length === 11) {
    return validarCedulaLuhn(digitos);
  }
  return false;
}